import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { AlertTriangle, PackageX, Clock, DoorClosed, Truck, MessageSquare } from 'lucide-react'
import type { DeepOrder, DeclineReason } from '../types'

interface DeclineOrderDialogProps {
  order: DeepOrder | null
  isOpen: boolean
  onClose: () => void
  onDecline: (reason: DeclineReason, customReason?: string) => void
  isLoading?: boolean
}

import type { LucideIcon } from 'lucide-react'

const DECLINE_REASONS: { key: DeclineReason; icon: LucideIcon }[] = [
  { key: 'out_of_stock',        icon: PackageX },
  { key: 'too_busy',            icon: Clock },
  { key: 'closing_soon',        icon: DoorClosed },
  { key: 'delivery_unavailable', icon: Truck },
  { key: 'other',               icon: MessageSquare },
]

export function DeclineOrderDialog({
  order,
  isOpen,
  onClose,
  onDecline,
  isLoading = false,
}: DeclineOrderDialogProps) {
  const { t } = useTranslation('orders')
  const [selectedReason, setSelectedReason] = useState<DeclineReason | null>(null)
  const [customReason, setCustomReason] = useState('')

  const handleDecline = () => {
    if (!selectedReason) return
    onDecline(selectedReason, selectedReason === 'other' ? customReason.trim() : undefined)
    resetState()
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const resetState = () => {
    setSelectedReason(null)
    setCustomReason('')
  }

  if (!order) return null

  const isValid = selectedReason && (selectedReason !== 'other' || customReason.trim())

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-status-warning">
            <AlertTriangle className="h-5 w-5" />
            {t('confirmation.declineTitle')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('confirmation.declineDescription', { id: order.id })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Tablet: larger padding and spacing */}
        <div className="py-4 tablet:py-5 space-y-4 tablet:space-y-5">
          <div>
            <Label className="text-sm tablet:text-kitchen-sm font-medium mb-3 tablet:mb-4 block">
              {t('confirmation.selectReason')}
            </Label>

            {/* Tablet: larger touch targets for radio items */}
            <RadioGroup
              value={selectedReason ?? ''}
              onValueChange={(value) => setSelectedReason(value as DeclineReason)}
              className="space-y-2 tablet:space-y-3"
            >
              {DECLINE_REASONS.map(({ key, icon: Icon }) => (
                <div key={key} className="flex items-center space-x-2 tablet:space-x-3 tablet:min-h-touch tablet:py-1">
                  <RadioGroupItem
                    value={key}
                    id={key}
                    disabled={isLoading}
                    className="tablet:h-5 tablet:w-5"
                  />
                  <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Label htmlFor={key} className="cursor-pointer font-normal tablet:text-kitchen-sm">
                    {t(`confirmation.reasons.${key}`)}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {selectedReason === 'other' && (
            <div>
              <Label htmlFor="custom-reason" className="text-sm tablet:text-kitchen-sm font-medium">
                {t('confirmation.customReasonLabel')}
              </Label>
              <Textarea
                id="custom-reason"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder={t('confirmation.customReasonPlaceholder')}
                // Tablet: larger text area
                className="mt-2 tablet:mt-3 tablet:text-kitchen-sm tablet:min-h-[80px]"
                rows={2}
                disabled={isLoading}
              />
            </div>
          )}

          <p className="text-sm tablet:text-kitchen-sm text-muted-foreground bg-status-warning/10 p-3 tablet:p-4 rounded-md border border-status-warning/20">
            {t('confirmation.declineWarning')}
          </p>
        </div>

        {/* Tablet: larger footer buttons with touch-friendly sizing */}
        <AlertDialogFooter className="gap-2 sm:gap-0 tablet:gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
            className="tablet:min-h-touch tablet:text-kitchen-sm tablet:px-6"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDecline}
            disabled={isLoading || !isValid}
            className="tablet:min-h-touch tablet:text-kitchen-sm tablet:px-6"
          >
            {t('confirmation.declineOrder')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
